import {Injectable, inject} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {Observable, tap} from 'rxjs';
import {AuthService} from '../../auth/auth.service';
import {environment} from '../../../environments/environment';

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const CSRF_COOKIE = 'csrf_token';
const CSRF_HEADER = 'X-CSRF-Token';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private auth = inject(AuthService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const headers: Record<string, string> = {};

    // In dev the token is held in memory and sent as a bearer header (the dev API
    // is schemeful-cross-site, so the auth cookie isn't sent locally). In prod the
    // token never enters JS — there's no token here and the httpOnly `jwt` cookie
    // authenticates via withCredentials below. Guard on the token itself so an
    // empty/blank token never produces a malformed `JWT ` header.
    const token = this.auth.user$.value?.token;
    if (token) {
      headers['Authorization'] = `JWT ${token}`;
    }

    // M8: double-submit CSRF. On state-changing methods echo the session's CSRF
    // token as X-CSRF-Token; the API enforces it only on cookie-authenticated
    // requests, so this is a no-op on the header path.
    //
    // The token comes from AuthService (the login response body), NOT from
    // document.cookie. This used to read the `csrf_token` cookie — which cannot
    // work: the cookie is host-only on the API domain, so at app.realguest.io
    // document.cookie never contains it. The header was silently never sent, and
    // every cookie-authenticated write 401'd. The cookie fallback below stays only
    // for a same-host deployment, where the cookie genuinely IS readable.
    if (UNSAFE_METHODS.has(request.method.toUpperCase())) {
      const csrf = this.auth.csrfToken ?? this.readCookie(CSRF_COOKIE);
      if (csrf) {
        headers[CSRF_HEADER] = csrf;
      }
    }

    const updateParams: {withCredentials: boolean; setHeaders?: Record<string, string>} = {
      withCredentials: true,
    };
    if (Object.keys(headers).length > 0) {
      updateParams.setHeaders = headers;
    }

    return next.handle(request.clone(updateParams)).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && request.url.startsWith(environment.apiUrl)) {
          this.followProfileLanguage(event.headers.get('Content-Language'));
        }
      })
    );
  }

  /**
   * The API stamps the saved profile language on every authenticated response.
   * Report it: HelperService switches the UI whenever it isn't the one on screen
   * (profile changed on another device or tab, or an auth-page flag moved it), so
   * the UI never shows a language the API isn't resolving labels in.
   */
  private followProfileLanguage(lang: string | null): void {
    if (lang && this.auth.user$.value) {
      this.auth.setProfileLanguage(lang);
    }
  }

  private readCookie(name: string): string | null {
    if (typeof document === 'undefined' || !document.cookie) {
      return null;
    }
    // `name` is a fixed literal (CSRF_COOKIE) with no regex metacharacters.
    const match = new RegExp('(?:^|; )' + name + '=([^;]*)').exec(document.cookie);
    return match ? decodeURIComponent(match[1]) : null;
  }
}
